import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { dispatchRequest } from '../dispatch-request';

import { uniq } from 'lodash';

import { Exception } from '../../core/models';
import { ToastService } from '../../core/services/toast.service';
import { updateEntities } from '../utils';

import {
  LoadExceptionsAction,
  LoadExceptionsSuccessAction,
  SaveExceptionAction,
  SaveExceptionFailAction,
  SaveExceptionSuccessAction,
  DeleteExceptionAction,
  DeleteExceptionSuccessAction,
} from './exception.actions';
import {
  defaultRequestState,
  IRequestsNestedState,
} from '../interfaces/requests.interface';

export interface ExceptionStateModel {
  entities: { [key: number]: Exception<any> };
  ids: number[];
  exceptionGetRequestState: IRequestsNestedState;
  saveExceptionPostRequestState: IRequestsNestedState;
  deleteExceptionPostRequestState: IRequestsNestedState;
}

@State<ExceptionStateModel>({
  name: 'exception',
  defaults: {
    entities: {},
    ids: [],
    exceptionGetRequestState: { ...defaultRequestState },
    saveExceptionPostRequestState: { ...defaultRequestState },
    deleteExceptionPostRequestState: { ...defaultRequestState },
  },
})
@Injectable()
export class ExceptionState {
  constructor(
    private httpClient: HttpClient,
    private toastService: ToastService,
  ) {}

  @Action(LoadExceptionsAction)
  loadExceptions(
    ctx: StateContext<ExceptionStateModel>,
    action: LoadExceptionsAction,
  ) {
    const request = this.httpClient.get('GetExceptions');

    // exceptionGetRequestState is deliberately left unwritten: ExceptionService
    // drives the global app spinner off its `loading` flag, so writing it would
    // put a spinner over every exceptions load.
    return dispatchRequest(request, ctx, LoadExceptionsSuccessAction);
  }

  @Action(LoadExceptionsSuccessAction)
  loadExceptionsSuccess(
    ctx: StateContext<ExceptionStateModel>,
    action: LoadExceptionsSuccessAction,
  ) {
    const stateUpdate = updateEntities(action.payload, Exception);
    ctx.patchState({
      entities: {
        ...ctx.getState().entities,
        ...stateUpdate.entities,
      },
      ids: uniq([
        ...ctx.getState().ids,
        ...stateUpdate.ids,
      ]),
    });
  }

  @Action(SaveExceptionAction)
  saveException(
    ctx: StateContext<ExceptionStateModel>,
    action: SaveExceptionAction,
  ) {
    const request = this.httpClient.post(`SaveException`, action.payload);

    return dispatchRequest(
      request,
      ctx,
      SaveExceptionSuccessAction,
      SaveExceptionFailAction,
      'saveExceptionPostRequestState',
    );
  }

  @Action(SaveExceptionSuccessAction)
  saveExceptionSuccess(
    ctx: StateContext<ExceptionStateModel>,
    action: SaveExceptionSuccessAction,
  ) {
    const exception = new Exception(action.payload);

    ctx.patchState({
      entities: {
        ...ctx.getState().entities,
        [exception.id]: exception,
      },
      ids: uniq([
        ...ctx.getState().ids,
        exception.id,
      ]),
    });

    this.toastService.showToast();
  }

  @Action(DeleteExceptionAction)
  deleteException(
    ctx: StateContext<ExceptionStateModel>,
    action: DeleteExceptionAction,
  ) {
    const request = this.httpClient.delete(`DeleteException?id=${action.payload}`);

    return dispatchRequest(request, ctx, DeleteExceptionSuccessAction);
  }

  @Action(DeleteExceptionSuccessAction)
  deleteExceptionSuccess(
    ctx: StateContext<ExceptionStateModel>,
    action: DeleteExceptionSuccessAction,
  ) {
    const exceptionId: number = action.payload;

    ctx.patchState({
      ids: ctx.getState().ids.filter(id => id !== exceptionId),
    });

    this.toastService.showToast();
  }
}
